import {
    ChangeDetectionStrategy, Component, Input, booleanAttribute
} from "@angular/core";
import { MatIcon } from "@angular/material/icon";

/**
 * A read-only, presentational echo of a field's value — for a base-only
 * field on a localized tab, where a live control isn't rendered but the
 * value still needs to be visible so the user knows what will be saved.
 *
 * Never disables an actual `FormControl` — see the Localized Domain Form
 * pattern's own rationale (`04-forms.md`) for why `.disable()` on a base
 * control is unsafe here. This component displays a value; it never holds
 * one.
 *
 * An empty value (`null`, `undefined` or `""`) renders as an em dash so the box keeps the
 * same height as its neighbours and the blank reads as intentional; `"0"` still shows as `0`.
 *
 * @example
 * ```html
 * <mgd-readonly-field label="Country" [value]="chain.country" locked />
 * ```
 */
@Component({
    selector: "mgd-readonly-field",
    templateUrl: "./readonly-field.component.html",
    styleUrl: "./readonly-field.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatIcon]
})
export class ReadonlyFieldComponent {

    /** The field's label, matching what the live control would show. */
    @Input({ required: true }) label: string;

    /** The value to display, already resolved to a displayable string. */
    @Input({ required: true }) value: string;

    /** Shows a lock icon, signaling this field is edited on another tab. */
    @Input({ transform: booleanAttribute }) locked = false;

    /** The value as shown: the em dash placeholder when the value is empty. */
    get displayValue(): string {
        return this.value === null || this.value === undefined || this.value === "" ? "\u2014" : this.value;
    }
}
